import clsx from 'clsx';
import React from 'react';
import countries from 'i18n-iso-countries';
import en from 'i18n-iso-countries/langs/en.json';
import Input from '../Input';
import Select from '../Select';
import Button from '../Button';
import { useButtonSizeSmall } from '../../utils/buttonSizes';
import PaymentCardIcon from '../../assets/icons/payment-card.svg?react';

interface UpdateCardDetailsModalProps {
  otherClasses?: string;
  formHandler: any;
  handleClose: () => void;
}

const UpdateCardDetailsModal: React.FC<UpdateCardDetailsModalProps> = ({
  otherClasses,
  formHandler,
  handleClose,
}) => {
  const updateCardDetailsModalClasses = clsx(otherClasses);
  countries.registerLocale(en);
  const countryNames = countries.getNames('en');

  const options = Object.entries(countryNames).map(([code, name]) => ({
    label: name,
    value: code,
  }));

  const sm = useButtonSizeSmall();

  return (
    <div
      className={updateCardDetailsModalClasses}
      data-testid="update-card-details-modal"
    >
      <div className="mb-8 flex w-full flex-col items-center gap-2.5 text-center lg:mb-12">
        <PaymentCardIcon className="[&>path]:fill-brand-byzantine/100 h-12 w-12 md:h-85 md:w-85" />
        <h4 className="text-brand-byzantine">Edit Card Details</h4>
        <p className="text-brand-byzantine font-inter-variable text-lg leading-5 font-normal">
          Update your card information below. Ensure all required fields (marked
          with *) are filled before saving.
        </p>
      </div>
      <form action="#" onSubmit={formHandler?.handleSubmit}>
        <Input
          onBlur={formHandler.handleBlur}
          label="Name*"
          errorMessage={
            formHandler?.touched.name && formHandler?.errors.name
              ? formHandler?.errors.name
              : ''
          }
          disabled={formHandler.isSubmitting}
          value={formHandler.values.name}
          type="text"
          placeholder="Enter name"
          onChange={formHandler.handleChange}
          name="name"
          otherClasses={{ container: 'w-full mb-6' }}
        />
        <div className="gird-cols-1 mb-6 grid gap-6 sm:grid-cols-2">
          <Input
            onBlur={formHandler.handleBlur}
            label="Address 1*"
            errorMessage={
              formHandler?.touched.line1 && formHandler?.errors.line1
                ? formHandler?.errors.line1
                : ''
            }
            disabled={formHandler.isSubmitting}
            value={formHandler.values.line1}
            type="text"
            placeholder="Enter address line 1"
            onChange={formHandler.handleChange}
            name="line1"
            otherClasses={{ container: 'w-full' }}
          />
          <Input
            onBlur={formHandler.handleBlur}
            label="Address 2*"
            errorMessage={
              formHandler?.touched.line2 && formHandler?.errors.line2
                ? formHandler?.errors.line2
                : ''
            }
            disabled={formHandler.isSubmitting}
            value={formHandler.values.line2}
            type="text"
            placeholder="Enter address line 2"
            onChange={formHandler.handleChange}
            name="line2"
            otherClasses={{ container: 'w-full' }}
          />
        </div>
        <div className="gird-cols-1 mb-6 grid gap-6 sm:grid-cols-2">
          <Select
            label="Country*"
            list={options}
            value={formHandler.values.country}
            disabled={formHandler.isSubmitting}
            placeholder="Select Country"
            otherClasses={{
              container: 'w-full',
              listContainer: 'bottom-18 top-auto',
            }}
            onSelect={(e) => formHandler?.setFieldValue('country', e, true)}
            errorMessage={
              formHandler?.touched.country && formHandler?.errors.country
                ? formHandler?.errors.country
                : ''
            }
            aria-required={true}
            aria-invalid={
              formHandler?.touched.country && formHandler?.errors.country
                ? true
                : false
            }
          />

          <Input
            onBlur={formHandler.handleBlur}
            label="State/Province*"
            errorMessage={
              formHandler?.touched.state && formHandler?.errors.state
                ? formHandler?.errors.state
                : ''
            }
            disabled={formHandler.isSubmitting}
            value={formHandler.values.state}
            type="text"
            placeholder="Enter state"
            onChange={formHandler.handleChange}
            name="state"
            otherClasses={{ container: 'w-full' }}
          />
        </div>
        <div className="gird-cols-1 mb-6 grid gap-6 sm:grid-cols-2">
          <Input
            onBlur={formHandler.handleBlur}
            label="City*"
            errorMessage={
              formHandler?.touched.city && formHandler?.errors.city
                ? formHandler?.errors.city
                : ''
            }
            disabled={formHandler.isSubmitting}
            value={formHandler.values.city}
            type="text"
            placeholder="Enter city"
            onChange={formHandler.handleChange}
            name="city"
            otherClasses={{ container: 'w-full' }}
          />
          <Input
            onBlur={formHandler.handleBlur}
            label="Postal Code*"
            errorMessage={
              formHandler?.touched.postal_code &&
              formHandler?.errors.postal_code
                ? formHandler?.errors.postal_code
                : ''
            }
            disabled={formHandler.isSubmitting}
            value={formHandler.values.postal_code}
            type="text"
            placeholder="Enter postal code"
            onChange={formHandler.handleChange}
            name="postal_code"
            otherClasses={{ container: 'w-full' }}
          />
        </div>
        <div className="mt-8 flex flex-col-reverse gap-15 md:grid md:grid-cols-2 md:gap-6 lg:mt-12">
          <Button
            variant="secondary"
            type="button"
            size={sm}
            onClick={() => handleClose()}
            otherClasses="text-brand-posh-pink hover:border-brand-posh-pink hover:text-brand-posh-pink"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size={sm}
            disable={
              (formHandler?.initialValues?.name === formHandler?.values?.name &&
                formHandler?.initialValues?.line1 ===
                  formHandler?.values?.line1 &&
                formHandler?.initialValues?.line2 ===
                  formHandler?.values?.line2 &&
                formHandler?.initialValues?.country ===
                  formHandler?.values?.country &&
                formHandler?.initialValues?.state ===
                  formHandler?.values?.state &&
                formHandler?.initialValues?.city ===
                  formHandler?.values?.city &&
                formHandler?.initialValues?.postal_code ===
                  formHandler?.values?.postal_code) ||
              formHandler?.isSubmitting
            }
          >
            Update Details
          </Button>
        </div>
      </form>
    </div>
  );
};

export default UpdateCardDetailsModal;
